import React, { useEffect, useRef, useState } from 'react';
import { useImmutableCallback } from '@agro1desenvolvimento/react-hooks';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Info } from 'lucide-react';
import {
  faArrowRotateRight,
  faCheck,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { faHourglass } from '@fortawesome/free-regular-svg-icons';
import { IntegrationError, IntegrationErrorStatus } from '../../@types';
import { formatStringDate } from '../utils';
import IntegrationErrorTooltipContent from './IntegrationErrorTooltipContent';

const tooltipHideRegistry = new Map<string, () => void>();

interface ErroItemProps {
  erro: IntegrationError;
  onResync: (id: string) => void;
}

function renderStatusIcon(status: IntegrationErrorStatus) {
  if (status === 'erro') {
    return (
      <FontAwesomeIcon
        icon={faTriangleExclamation}
        className="integration-error-item-icon integration-error-item-icon--erro"
      />
    );
  }

  if (status === 'confirmado') {
    return (
      <FontAwesomeIcon
        icon={faCheck}
        className="integration-error-item-icon integration-error-item-icon--confirmado"
      />
    );
  }

  if (status === 'processando') {
    return (
      <FontAwesomeIcon
        icon={faArrowRotateRight}
        className="integration-error-item-icon integration-error-item-icon--processando"
      />
    );
  }

  return (
    <FontAwesomeIcon
      icon={faHourglass}
      className="integration-error-item-icon integration-error-item-icon--pendente"
    />
  );
}

const ErroItemInfoIcon: React.FC<{ erro: IntegrationError }> = ({ erro }) => {
  const infoIconId = `integration-error-info-${erro.id}`;
  const tooltipRef = useRef<any>(null);

  useEffect(() => {
    tooltipHideRegistry.set(infoIconId, () => tooltipRef.current?.hide?.());
    return () => {
      tooltipHideRegistry.delete(infoIconId);
    };
  }, [infoIconId]);

  const handleMouseEnter = useImmutableCallback(() => {
    tooltipHideRegistry.forEach((hide, id) => {
      if (id !== infoIconId) hide();
    });
  });

  return (
    <>
      <span
        id={infoIconId}
        className="integration-error-item-info-icon"
        onMouseEnter={handleMouseEnter}
      >
        <Info size={14} />
      </span>
      <Tooltip
        ref={tooltipRef}
        target={`#${infoIconId}`}
        position="top"
        className="integration-error-item-info-tooltip"
        autoHide={false}
      >
        <IntegrationErrorTooltipContent erro={erro} />
      </Tooltip>
    </>
  );
};

const ErroItem: React.FC<ErroItemProps> = ({ erro, onResync }) => {
  const [resyncClicked, setResyncClicked] = useState(false);

  const handleResync = useImmutableCallback(() => {
    setResyncClicked(true);
    onResync(erro.id);
  });

  return (
    <div className="panel-list-item">
      <div className="integration-error-item-propriedade">
        {erro.propriedade}
      </div>

      <div className="panel-list-item-center">
        <div className="panel-list-item-title-row">
          {renderStatusIcon(erro.status)}
          {erro.status === 'erro' ? (
            <div className="panel-list-item-title integration-error-item-title-with-info">
              <span>{erro.entidade}</span>
              <ErroItemInfoIcon erro={erro} />
            </div>
          ) : (
            <span className="panel-list-item-title">{erro.entidade}</span>
          )}
        </div>
        <div className="panel-list-item-date">
          {formatStringDate(erro.createdAt)}
        </div>
      </div>

      <div className="panel-list-item-actions">
        {erro.status === 'erro' && (
          <Button
            icon={<FontAwesomeIcon icon={faArrowRotateRight} />}
            className="p-button-rounded integration-error-item-btn-refresh"
            disabled={resyncClicked}
            onClick={handleResync}
          />
        )}
      </div>
    </div>
  );
};

export default React.memo(ErroItem);
